import { createMarketState, marketPublicView, type MarketState } from "./market-core";
import { createInitialCatanState, catanPublicView, type CatanState } from "./catan-core";
import { createInitialCoralRoomState, coralRoomPublicView, type CoralRoomState } from "./coral-chorus-room";
import { createInitialGlassRoomState, glassRoomPublicView, type GlassRoomState } from "./stained-glass-room";
import { freshRoomChat, type RoomChatState } from "./room-chat";
import { participatingPlayers } from "./room-participation";
import { canViewObserverHoldings, observerHoldings } from "./observer-holdings";
import { pokerHistoryPublicView, snapshotPokerPrivateHands } from "./poker-history";
import { TABOO_CARDS } from "./taboo-cards";
export { canUseRoomControl } from "./room-controls";
import type { PokerCard } from "./poker-core";
import { blackjackHandValue, canSplitBlackjackPair, hiLoCardValue, type BlackjackCard, type BlackjackOutcome } from "./blackjack-core";
import { isDeadSequenceCard, legalSequenceSpaces, sequenceUpcomingTurns, type SequenceCard, type SequenceTeam } from "./sequence-core";
import {
  MONOPOLY_COLORS,
  monopolyCompleteColors,
  type MonopolyCard,
  type MonopolyColor,
  type MonopolyImprovement,
  type MonopolyPlacedProperty,
} from "./monopoly-deal-core";
import {
  KING_TOKYO_CARD_MAP,
  KING_TOKYO_ALL_CARDS,
  KING_TOKYO_COSTUMES,
  KING_TOKYO_DIE_FACES,
  KING_TOKYO_CARDS,
  KING_TOKYO_EVOLUTION_MAP,
  KING_TOKYO_EVOLUTIONS,
  KING_TOKYO_MONSTERS,
  KING_TOKYO_WICKEDNESS_MAP,
  KING_TOKYO_WICKEDNESS_TILES,
  createInitialKingTokyoState,
  type KingTokyoCard,
  type KingTokyoDie,
  type KingTokyoEvolution,
  type KingTokyoMonsterId,
  type KingTokyoPlayerState,
  type KingTokyoRecoverySnapshotSummary,
  type KingTokyoState,
  type KingTokyoWickednessTile,
} from "./king-tokyo-core";
import { createInitialHeartsState, heartsPublicView, type HeartsState } from "./hearts-core";
import { createInitialSpadesState, spadesPublicView, type SpadesState } from "./spades-core";
import { createInitialUnoState, unoPublicView, type UnoState } from "./uno-core";
import { createInitialPartyPetsState, publicPartyPetsState, type PartyPetsState } from "./party-pets-core";
import { createInitialFamilyFeudState, familyFeudMultiplier, type FamilyFeudState } from "./family-feud-core";
import { FAMILY_FEUD_ROUNDS } from "./family-feud-rounds";
import { createInitialHexchainState, publicHexchainState, type HexchainState } from "./hexchain-core";
import { connectFourPublicView, createInitialConnectFourState, type ConnectFourState } from "./connect-four-core";
import { createInitialPuzzleFighterState, puzzleFighterPublicView, type PuzzleFighterState } from "./puzzle-fighter-core";
import { createInitialTicketToRideState, ticketToRidePublicView, type TicketToRideState } from "./ticket-to-ride-core";
import { axisAlliesPublicView, createInitialAxisAlliesState, type AxisAlliesState } from "./axis-allies-core";
import { createInitialStrategoState, strategoPublicView, type StrategoState } from "./stratego-core";
import { createInitialModernArtState, modernArtPublicView, type ModernArtState } from "./modern-art-core";
import {
  KING_COURT_GAME_TYPE,
  KING_COURT_TEAMS,
  courtifyText,
  kingCourtCard,
  kingCourtEvolution,
  kingCourtWickedness,
} from "./king-court-theme";

export type QuestionRecord = {
  id: string;
  category: Category;
  prompt: string;
  choices: string[];
  correctIndex: number;
  difficulty: "easy" | "medium" | "hard";
  era: string;
  sourceUrl?: string;
  sourceTitle?: string;
  sourceCapturedAt?: string;
  scoreType?: "critics" | "audience";
  scoreValue?: number;
  scoreCapturedAt?: string;
  numericAnswer?: number;
  answerUnit?: string;
  answerDetail?: string;
};

export const TRIVIA_CATEGORIES = ["eighties_music", "nineties_music", "two_thousands_music", "twenty_tens_music", "food", "history", "entertainment", "sports", "business", "rotten_tomatoes", "lior", "dogs", "animated_movies", "berkeley", "warriors", "star_trek"] as const;
export const CATEGORIES = [...TRIVIA_CATEGORIES, "word_match", "impostor", "closest_without_over", "liars_dice", "taboo", "codenames", "sequence", "holdem", "blackjack", "hearts", "spades", "uno", "monopoly_deal", "king_of_tokyo", KING_COURT_GAME_TYPE, "turbo_rally", "party_pets", "family_feud", "hexchain", "connect_four", "puzzle_fighter", "ticket_to_ride", "axis_allies", "stratego", "modern_art", "market_wars", "catan", "coral_chorus", "stained_glass"] as const;
export type Category = typeof CATEGORIES[number];
export const CATEGORY_LABELS: Record<Category, string> = {
  eighties_music: "80s Music",
  nineties_music: "90s Music",
  two_thousands_music: "2000s Music",
  twenty_tens_music: "2010s Music",
  food: "Food",
  history: "History",
  entertainment: "Entertainment",
  sports: "Sports",
  business: "Business",
  rotten_tomatoes: "Rotten Tomatoes",
  lior: "Lior",
  dogs: "Dogs",
  animated_movies: "Animated Movies",
  berkeley: "Berkeley",
  warriors: "Golden State Warriors",
  star_trek: "Star Trek",
  word_match: "Apples to Apples",
  impostor: "One-Word Impostor",
  closest_without_over: "Closest Without Going Over",
  liars_dice: "Liar’s Dice",
  taboo: "Taboo",
  codenames: "Code Names",
  sequence: "Sequence",
  holdem: "Texas Hold’em",
  blackjack: "Blackjack",
  hearts: "Hearts",
  spades: "Spades",
  uno: "Uno",
  monopoly_deal: "Monopoly Deal",
  king_of_tokyo: "King of Tokyo",
  king_of_the_court: "King of the Court",
  turbo_rally: "Turbo Rally",
  party_pets: "Party Pets",
  family_feud: "Family Feud",
  hexchain: "Hexchain Siege",
  connect_four: "Connect Four",
  puzzle_fighter: "Puzzle Fighter",
  ticket_to_ride: "Ticket to Ride",
  axis_allies: "Axis & Allies",
  stratego: "Stratego",
  modern_art: "Modern Art",
  market_wars: "Wall Street: Market Wars",
  catan: "Settlers of Catan",
  coral_chorus: "Coral Chorus",
  stained_glass: "Stained Glass Together",
};

export type Player = {
  id: string;
  name: string;
  score: number;
  connected: boolean;
  sittingOut?: boolean;
  joinedAt: number;
};

export const GAME_MODES = ["closest", "without_over", "multiple_choice", "judge_choice", "impostor_vote", "liars_dice", "taboo", "codenames", "sequence", "holdem", "blackjack", "hearts", "spades", "uno", "monopoly_deal", "king_of_tokyo", KING_COURT_GAME_TYPE, "turbo_rally", "party_pets", "family_feud", "hexchain", "connect_four", "puzzle_fighter", "ticket_to_ride", "axis_allies", "stratego", "modern_art", "market_wars", "catan", "coral_chorus", "stained_glass"] as const;
export type GameMode = typeof GAME_MODES[number];
export type GameType = "trivia" | "word_match" | "impostor" | "closest" | "liars_dice" | "taboo" | "codenames" | "sequence" | "holdem" | "blackjack" | "hearts" | "spades" | "uno" | "monopoly_deal" | "king_of_tokyo" | "king_of_the_court" | "turbo_rally" | "party_pets" | "family_feud" | "hexchain" | "connect_four" | "puzzle_fighter" | "ticket_to_ride" | "axis_allies" | "stratego" | "modern_art" | "market_wars" | "catan" | "coral_chorus" | "stained_glass";

export type KartSnapshot = {
  playerId: string;
  x: number;
  y: number;
  angle: number;
  speed: number;
  lap: number;
  progress: number;
  checkpoint: number;
  item: string | null;
  finishedAt: number | null;
  updatedAt: number;
};

export type KartEvent = {
  id: string;
  playerId: string;
  kind: "spark" | "slick" | "shield" | "boost";
  targetId?: string;
  x?: number;
  y?: number;
  createdAt: number;
};

export type KartRaceState = {
  trackId: string;
  laps: number;
  drivers: Record<string, string>;
  racerIds: string[];
  racers: Record<string, KartSnapshot>;
  events: KartEvent[];
  startAt: number | null;
  finishOrder: string[];
};

export type MonopolyPendingAction = {
  kind: "rent" | "debt_collector" | "birthday" | "sly_deal" | "forced_deal" | "deal_breaker";
  sourceId: string;
  targetIds: string[];
  targetIndex: number;
  targetCardId?: string;
  offeredCardId?: string;
  color?: MonopolyColor;
  amount?: number;
  multiplier: number;
  noCount: number;
  responderId: string;
};

export type MonopolyPendingPayment = { debtorId: string; creditorId: string; amount: number };

export type MonopolyPlayerStats = {
  rentCollected: number;
  biggestRent: number;
  propertiesStolen: number;
  completeSetsStolen: number;
};

export type MonopolyDealState = {
  cards: Record<string, MonopolyCard>;
  deck: string[];
  discard: string[];
  hands: Record<string, string[]>;
  banks: Record<string, string[]>;
  properties: Record<string, MonopolyPlacedProperty[]>;
  improvements: Record<string, MonopolyImprovement[]>;
  playerOrder: string[];
  currentPlayerIndex: number;
  playsThisTurn: number;
  pendingAction: MonopolyPendingAction | null;
  pendingPayment: MonopolyPendingPayment | null;
  winnerId: string | null;
  lastAction: string;
  log: string[];
  stats: Record<string, MonopolyPlayerStats>;
};

export type SequenceState = {
  teamCount: 2 | 3;
  teams: Record<string, SequenceTeam>;
  teamScores: Record<SequenceTeam, number>;
  targetSequences: number;
  board: Array<SequenceCard | null>;
  chips: Array<SequenceTeam | null>;
  locked: boolean[];
  deck: SequenceCard[];
  discard: SequenceCard[];
  hands: Record<string, SequenceCard[]>;
  playerOrder: string[];
  currentPlayerIndex: number;
  teamTurnIndices: Record<SequenceTeam, number>;
  turnNumber: number;
  claimedLineKeys: string[];
  winnerTeam: SequenceTeam | null;
  lastMove: { playerId: string; playerName: string; team: SequenceTeam; card: SequenceCard; boardIndex: number | null; action: "place" | "remove" | "swap"; sequencesMade: number } | null;
};

export type BlackjackPlayerStatus = "playing" | "stood" | "busted" | "blackjack";
export type BlackjackResult = {
  playerId: string;
  playerName: string;
  handIndex: number;
  outcome: BlackjackOutcome;
  playerTotal: number;
  dealerTotal: number;
  bet: number;
  payout: number;
  net: number;
  blackjack: boolean;
};
export type BlackjackState = {
  startingBankroll: number;
  defaultBet: number;
  roundNumber: number;
  deck: BlackjackCard[];
  shuffleCount: number;
  reshuffledThisRound: boolean;
  showCardCount: boolean;
  runningCount: number;
  dealerHoleShuffleCount: number | null;
  playerOrder: string[];
  stacks: Record<string, number>;
  bets: Record<string, number>;
  hasBet: Record<string, boolean>;
  hands: Record<string, BlackjackCard[][]>;
  handBets: Record<string, number[]>;
  statuses: Record<string, BlackjackPlayerStatus[]>;
  fromSplit: Record<string, boolean[]>;
  insuranceBets: Record<string, number>;
  insuranceDecisions: Record<string, boolean>;
  insurancePayouts: Record<string, number>;
  dealerHand: BlackjackCard[];
  currentPlayerId: string | null;
  currentHandIndex: number;
  results: BlackjackResult[];
};

export type PokerStreet = "preflop" | "flop" | "turn" | "river" | "showdown";
export type PokerPlayerStatus = "active" | "folded" | "all_in" | "out";
export type PokerAction = {
  playerId: string;
  playerName: string;
  type: "small_blind" | "big_blind" | "fold" | "check" | "call" | "bet" | "raise" | "all_in";
  amount: number;
  toAmount?: number;
  street: PokerStreet;
};
export type PokerAward = { playerId: string; playerName: string; amount: number; handLabel: string };
export type PokerPrivateHand = { playerId: string; playerName: string; cards: PokerCard[]; status: PokerPlayerStatus };
export type PokerHandHistory = {
  handNumber: number;
  startedAt: number;
  completedAt: number;
  dealerId: string;
  dealerName: string;
  smallBlind: number;
  bigBlind: number;
  seatOrder: Array<{ playerId: string; playerName: string }>;
  board: PokerCard[];
  actions: PokerAction[];
  awards: PokerAward[];
  shownHands: Array<{ playerId: string; playerName: string; cards: PokerCard[]; handLabel: string }>;
  totalPot: number;
  privateHands?: PokerPrivateHand[];
};
export type PokerPublicHandHistory = Omit<PokerHandHistory, "privateHands"> & { observerHands?: PokerPrivateHand[] };
export type PokerState = {
  smallBlind: number;
  bigBlind: number;
  startingStack: number;
  seatOrder: string[];
  stacks: Record<string, number>;
  statuses: Record<string, PokerPlayerStatus>;
  dealerIndex: number;
  handNumber: number;
  deck: PokerCard[];
  board: PokerCard[];
  holeCards: Record<string, PokerCard[]>;
  street: PokerStreet;
  currentPlayerId: string | null;
  currentBet: number;
  minimumRaise: number;
  streetBets: Record<string, number>;
  contributions: Record<string, number>;
  acted: Record<string, boolean>;
  actions: PokerAction[];
  startedAt: number;
  result: { awards: PokerAward[]; shownHands: Array<{ playerId: string; playerName: string; cards: PokerCard[]; handLabel: string }>; totalPot: number } | null;
  history: PokerHandHistory[];
};

export type CodeNamesTeam = "red" | "blue";
export type CodeNamesRole = CodeNamesTeam | "neutral" | "assassin";

export type CodeNamesCard = {
  word: string;
  role: CodeNamesRole;
  revealed: boolean;
};

export type CodeNamesState = {
  teamMode: "random" | "manual";
  teams: Record<string, CodeNamesTeam>;
  spymasters: Record<CodeNamesTeam, string | null>;
  board: CodeNamesCard[];
  activeTeam: CodeNamesTeam | null;
  startingTeam: CodeNamesTeam | null;
  clue: { word: string; count: number } | null;
  clueHistory: Array<{ team: CodeNamesTeam; word: string; count: number; turnNumber: number }>;
  guessesLeft: number;
  bonusGuessAvailable: boolean;
  turnNumber: number;
  winnerTeam: CodeNamesTeam | null;
  lastGuess: { index: number; role: CodeNamesRole; playerId: string; playerName: string } | null;
};

export type TabooTeam = "a" | "b";

export type TabooTurnResult = {
  clueGiverId: string;
  clueGiverName: string;
  team: TabooTeam;
  correct: number;
  skipped: number;
  taboos: number;
};

export type TabooState = {
  teamMode: "random" | "manual";
  teams: Record<string, TabooTeam>;
  teamScores: Record<TabooTeam, number>;
  targetScore: number;
  roundSeconds: number;
  roundNumber: number;
  activeTeam: TabooTeam | null;
  clueGiverId: string | null;
  turnOrder: string[];
  turnIndex: number;
  deck: number[];
  currentCardIndex: number | null;
  correctThisTurn: number;
  skippedThisTurn: number;
  taboosThisTurn: number;
  lastAction: "correct" | "skip" | "taboo" | null;
  result: TabooTurnResult | null;
};

export type WordSubmission = {
  id: string;
  playerId: string;
  playerName: string;
  card: string;
};

export type WordMatchState = {
  targetScore: number;
  roundNumber: number;
  judgeId: string | null;
  greenCard: string | null;
  hands: Record<string, string[]>;
  submissions: WordSubmission[];
  winner: WordSubmission | null;
  redDeck: string[];
  greenDeck: string[];
};

export type ImpostorClue = {
  playerId: string;
  playerName: string;
  clue: string;
  verbal?: boolean;
};

export type ImpostorVote = {
  voterId: string;
  targetId: string;
};

export type ImpostorResult = {
  impostorId: string;
  impostorName: string;
  topic: string;
  caught: boolean;
  correctVoterIds: string[];
  mostVotedIds: string[];
  tally: Record<string, number>;
};

export type ImpostorState = {
  targetScore: number;
  roundNumber: number;
  eligiblePlayerIds: string[];
  clueOrder: string[];
  currentClueIndex: number;
  impostorId: string | null;
  topic: string | null;
  topicCategory: string | null;
  clues: ImpostorClue[];
  votes: ImpostorVote[];
  result: ImpostorResult | null;
  usedTopics: string[];
};

export type DiceBid = {
  quantity: number;
  face: number;
  playerId: string;
  playerName: string;
};

export type LiarsDiceResult = {
  bidderId: string;
  bidderName: string;
  challengerId: string;
  challengerName: string;
  loserId: string;
  loserName: string;
  bid: DiceBid;
  actualCount: number;
  bidWasTrue: boolean;
  revealedHands: Array<{ playerId: string; playerName: string; dice: number[] }>;
};

export type LiarsDiceState = {
  roundNumber: number;
  diceCounts: Record<string, number>;
  hands: Record<string, number[]>;
  eligiblePlayerIds: string[];
  currentPlayerId: string | null;
  currentBid: DiceBid | null;
  bidHistory: DiceBid[];
  nextStarterId: string | null;
  result: LiarsDiceResult | null;
};

export type AnswerResult = {
  playerId: string;
  playerName: string;
  selectedIndex: number;
  correctIndex: number;
  correct: boolean;
  guess?: number;
  distance?: number;
  skipped?: boolean;
  winnerIds?: string[];
  targetValue?: number;
};

export type AnswerAttempt = {
  playerId: string;
  playerName: string;
  selectedIndex: number;
  correct: boolean;
  answeredAt: number;
  guess?: number;
  distance?: number;
  skipped?: boolean;
};

export type GameState = {
  partyNight?: import("./party-night").PartyNight | null;
  savedRoom?: import("./saved-library").SavedRoom | null;
  chat: RoomChatState;
  exists: boolean;
  roomCode: string;
  adminId: string;
  adminToken: string;
  /** Legacy persisted field; rematches are always available to every player. */
  adminOnlyPostGameControls: boolean;
  gameType: GameType;
  category: Category;
  gameMode: GameMode;
  questionCount: number;
  gameSessionId: string | null;
  resultsLogged: boolean;
  phase: "menu" | "lobby" | "question" | "result" | "submitting" | "judging" | "round_result" | "clueing" | "voting" | "impostor_result" | "dice_bidding" | "dice_result" | "taboo_ready" | "taboo_turn" | "taboo_result" | "codenames_clue" | "codenames_guess" | "sequence_turn" | "poker_betting" | "poker_result" | "blackjack_betting" | "blackjack_insurance" | "blackjack_playing" | "blackjack_result" | "hearts_passing" | "hearts_playing" | "hearts_round_result" | "spades_bidding" | "spades_playing" | "spades_round_result" | "uno_playing" | "uno_round_result" | "monopoly_turn" | "tokyo_roll" | "tokyo_yield" | "tokyo_costume" | "tokyo_buy" | "kart_countdown" | "kart_racing" | "party_live" | "party_minigame" | "party_finale" | "feud_board" | "feud_round_result" | "hexchain_battle" | "connect_four_playing" | "puzzle_fighter_playing" | "ticket_to_ride_playing" | "axis_allies_playing" | "stratego_playing" | "modern_art_playing" | "market_wars_playing" | "catan_playing" | "coral_chorus_playing" | "stained_glass_playing" | "finished";
  players: Record<string, Player>;
  questionIds: string[];
  questionNumber: number;
  deadline: number | null;
  result: AnswerResult | null;
  attempts: AnswerAttempt[];
  usedQuestionIds: string[];
  lastActive: number;
  wordMatch: WordMatchState;
  impostor: ImpostorState;
  liarsDice: LiarsDiceState;
  taboo: TabooState;
  codeNames: CodeNamesState;
  sequence: SequenceState;
  poker: PokerState;
  blackjack: BlackjackState;
  hearts: HeartsState;
  spades: SpadesState;
  uno: UnoState;
  monopolyDeal: MonopolyDealState;
  kingTokyo: KingTokyoState;
  kartRace: KartRaceState;
  partyPets: PartyPetsState;
  familyFeud: FamilyFeudState;
  hexchain: HexchainState;
  connectFour: ConnectFourState;
  puzzleFighter: PuzzleFighterState;
  ticketToRide: TicketToRideState;
  axisAllies: AxisAlliesState;
  stratego: StrategoState;
  modernArt: ModernArtState;
  marketWars: MarketState;
  catan: CatanState;
  coralChorus: CoralRoomState;
  stainedGlass: GlassRoomState;
};

export const DEFAULT_QUESTION_COUNT = 10;
export const QUESTION_TIME_MS = 15_000;
export const RESULT_TIME_MS = 3_000;
export const ROOM_TTL_MS = 2 * 60 * 60 * 1000;

export function freshState(roomCode = "", gameType: GameType = "trivia", adminId = "", adminToken = "", _legacyAdminOnlyPostGameControls = false): GameState {
  return {
    chat: freshRoomChat(),
    exists: false,
    roomCode,
    adminId,
    adminToken,
    adminOnlyPostGameControls: false,
    gameType,
    category: gameType === "word_match" ? "word_match" : gameType === "impostor" ? "impostor" : gameType === "closest" ? "closest_without_over" : gameType === "liars_dice" ? "liars_dice" : gameType === "taboo" ? "taboo" : gameType === "codenames" ? "codenames" : gameType === "sequence" ? "sequence" : gameType === "holdem" ? "holdem" : gameType === "blackjack" ? "blackjack" : gameType === "hearts" ? "hearts" : gameType === "spades" ? "spades" : gameType === "uno" ? "uno" : gameType === "monopoly_deal" ? "monopoly_deal" : gameType === "king_of_tokyo" ? "king_of_tokyo" : gameType === KING_COURT_GAME_TYPE ? KING_COURT_GAME_TYPE : gameType === "turbo_rally" ? "turbo_rally" : gameType === "party_pets" ? "party_pets" : gameType === "family_feud" ? "family_feud" : gameType === "hexchain" ? "hexchain" : gameType === "connect_four" ? "connect_four" : gameType === "puzzle_fighter" ? "puzzle_fighter" : gameType === "ticket_to_ride" ? "ticket_to_ride" : gameType === "axis_allies" ? "axis_allies" : gameType === "stratego" ? "stratego" : gameType === "modern_art" ? "modern_art" : gameType === "market_wars" ? "market_wars" : gameType === "catan" ? "catan" : gameType === "coral_chorus" ? "coral_chorus" : gameType === "stained_glass" ? "stained_glass" : "rotten_tomatoes",
    gameMode: gameType === "word_match" ? "judge_choice" : gameType === "impostor" ? "impostor_vote" : gameType === "closest" ? "without_over" : gameType === "liars_dice" ? "liars_dice" : gameType === "taboo" ? "taboo" : gameType === "codenames" ? "codenames" : gameType === "sequence" ? "sequence" : gameType === "holdem" ? "holdem" : gameType === "blackjack" ? "blackjack" : gameType === "hearts" ? "hearts" : gameType === "spades" ? "spades" : gameType === "uno" ? "uno" : gameType === "monopoly_deal" ? "monopoly_deal" : gameType === "king_of_tokyo" ? "king_of_tokyo" : gameType === KING_COURT_GAME_TYPE ? KING_COURT_GAME_TYPE : gameType === "turbo_rally" ? "turbo_rally" : gameType === "party_pets" ? "party_pets" : gameType === "family_feud" ? "family_feud" : gameType === "hexchain" ? "hexchain" : gameType === "connect_four" ? "connect_four" : gameType === "puzzle_fighter" ? "puzzle_fighter" : gameType === "ticket_to_ride" ? "ticket_to_ride" : gameType === "axis_allies" ? "axis_allies" : gameType === "stratego" ? "stratego" : gameType === "modern_art" ? "modern_art" : gameType === "market_wars" ? "market_wars" : gameType === "catan" ? "catan" : gameType === "coral_chorus" ? "coral_chorus" : gameType === "stained_glass" ? "stained_glass" : "closest",
    questionCount: ["word_match", "impostor", "liars_dice", "family_feud"].includes(gameType) ? 5 : gameType === "taboo" ? 15 : gameType === "codenames" ? 25 : gameType === "connect_four" ? 42 : DEFAULT_QUESTION_COUNT,
    gameSessionId: null,
    resultsLogged: true,
    phase: "lobby",
    players: {},
    questionIds: [],
    questionNumber: 0,
    deadline: null,
    result: null,
    attempts: [],
    usedQuestionIds: [],
    lastActive: Date.now(),
    wordMatch: {
      targetScore: 5,
      roundNumber: 0,
      judgeId: null,
      greenCard: null,
      hands: {},
      submissions: [],
      winner: null,
      redDeck: [],
      greenDeck: [],
    },
    impostor: {
      targetScore: 5,
      roundNumber: 0,
      eligiblePlayerIds: [],
      clueOrder: [],
      currentClueIndex: 0,
      impostorId: null,
      topic: null,
      topicCategory: null,
      clues: [],
      votes: [],
      result: null,
      usedTopics: [],
    },
    liarsDice: {
      roundNumber: 0,
      diceCounts: {},
      hands: {},
      eligiblePlayerIds: [],
      currentPlayerId: null,
      currentBid: null,
      bidHistory: [],
      nextStarterId: null,
      result: null,
    },
    taboo: {
      teamMode: "random",
      teams: {},
      teamScores: { a: 0, b: 0 },
      targetScore: 15,
      roundSeconds: 60,
      roundNumber: 0,
      activeTeam: null,
      clueGiverId: null,
      turnOrder: [],
      turnIndex: 0,
      deck: [],
      currentCardIndex: null,
      correctThisTurn: 0,
      skippedThisTurn: 0,
      taboosThisTurn: 0,
      lastAction: null,
      result: null,
    },
    codeNames: {
      teamMode: "random",
      teams: {},
      spymasters: { red: null, blue: null },
      board: [],
      activeTeam: null,
      startingTeam: null,
      clue: null,
      clueHistory: [],
      guessesLeft: 0,
      bonusGuessAvailable: false,
      turnNumber: 0,
      winnerTeam: null,
      lastGuess: null,
    },
    sequence: {
      teamCount: 2,
      teams: {},
      teamScores: { coral: 0, aqua: 0, gold: 0 },
      targetSequences: 2,
      board: [],
      chips: [],
      locked: [],
      deck: [],
      discard: [],
      hands: {},
      playerOrder: [],
      currentPlayerIndex: 0,
      teamTurnIndices: { coral: -1, aqua: -1, gold: -1 },
      turnNumber: 0,
      claimedLineKeys: [],
      winnerTeam: null,
      lastMove: null,
    },
    poker: {
      smallBlind: 50,
      bigBlind: 100,
      startingStack: 10_000,
      seatOrder: [],
      stacks: {},
      statuses: {},
      dealerIndex: -1,
      handNumber: 0,
      deck: [],
      board: [],
      holeCards: {},
      street: "preflop",
      currentPlayerId: null,
      currentBet: 0,
      minimumRaise: 100,
      streetBets: {},
      contributions: {},
      acted: {},
      actions: [],
      startedAt: 0,
      result: null,
      history: [],
    },
    blackjack: {
      startingBankroll: 10_000,
      defaultBet: 1_000,
      roundNumber: 0,
      deck: [],
      shuffleCount: 0,
      reshuffledThisRound: false,
      showCardCount: true,
      runningCount: 0,
      dealerHoleShuffleCount: null,
      playerOrder: [],
      stacks: {},
      bets: {},
      hasBet: {},
      hands: {},
      handBets: {},
      statuses: {},
      fromSplit: {},
      insuranceBets: {},
      insuranceDecisions: {},
      insurancePayouts: {},
      dealerHand: [],
      currentPlayerId: null,
      currentHandIndex: 0,
      results: [],
    },
    hearts: createInitialHeartsState(),
    spades: createInitialSpadesState(),
    uno: createInitialUnoState(),
    monopolyDeal: {
      cards: {}, deck: [], discard: [], hands: {}, banks: {}, properties: {}, improvements: {},
      playerOrder: [], currentPlayerIndex: 0, playsThisTurn: 0, pendingAction: null, pendingPayment: null,
      winnerId: null, lastAction: "", log: [], stats: {},
    },
    kingTokyo: createInitialKingTokyoState(),
    kartRace: {
      trackId: "neon_harbor",
      laps: 3,
      drivers: {},
      racerIds: [],
      racers: {},
      events: [],
      startAt: null,
      finishOrder: [],
    },
    partyPets: createInitialPartyPetsState(),
    familyFeud: createInitialFamilyFeudState(),
    hexchain: createInitialHexchainState(),
    connectFour: createInitialConnectFourState(),
    puzzleFighter: createInitialPuzzleFighterState(),
    ticketToRide: createInitialTicketToRideState(),
    axisAllies: createInitialAxisAlliesState(),
    stratego: createInitialStrategoState(),
    modernArt: createInitialModernArtState(),
    marketWars: createMarketState(),
    catan: createInitialCatanState(),
    coralChorus: createInitialCoralRoomState(),
    stainedGlass: createInitialGlassRoomState(),
  };
}

export function isHigherDiceBid(next: Pick<DiceBid, "quantity" | "face">, current: Pick<DiceBid, "quantity" | "face"> | null, totalDice: number) {
  if (!Number.isInteger(next.quantity) || next.quantity < 1 || next.quantity > totalDice) return false;
  if (!Number.isInteger(next.face) || next.face < 1 || next.face > 6) return false;
  return !current || next.quantity > current.quantity || (next.quantity === current.quantity && next.face > current.face);
}

export function countMatchingDice(hands: Record<string, number[]>, playerIds: string[], face: number) {
  return playerIds.reduce((total, playerId) => total + (hands[playerId] || []).filter((die) => die === face).length, 0);
}

export function validateKartProgress(
  previous: Pick<KartSnapshot, "lap" | "progress" | "checkpoint"> | undefined,
  requestedLap: number,
  requestedProgress: number,
  requestedCheckpoint: number,
) {
  if (!previous) return { lap: 1, checkpoint: 1 };
  let lap = previous.lap;
  let checkpoint = previous.checkpoint || 1;
  const checkpointWindows: Record<number, [number, number]> = { 2: [.18, .38], 3: [.43, .63], 4: [.68, .88] };
  const window = checkpointWindows[requestedCheckpoint];
  if (requestedLap === lap && requestedCheckpoint === checkpoint) {
    return { lap, checkpoint };
  }
  if (requestedLap === lap && requestedCheckpoint === checkpoint + 1 && window && requestedProgress >= window[0] && requestedProgress <= window[1]) {
    checkpoint = requestedCheckpoint;
  } else if (checkpoint === 4 && requestedCheckpoint === 1 && requestedLap === lap + 1 && previous.progress > .85 && requestedProgress < .15) {
    lap = requestedLap;
    checkpoint = 1;
  }
  return { lap, checkpoint };
}

export function switchPartyGame(state: GameState, gameType: GameType, phase: "menu" | "lobby" = "lobby"): GameState {
  const next = freshState(state.roomCode, gameType, state.adminId, state.adminToken, state.adminOnlyPostGameControls);
  next.partyNight = state.partyNight;
  next.chat = state.chat;
  next.exists = state.exists;
  next.phase = phase;
  next.players = Object.fromEntries(Object.values(state.players).map((player) => [player.id, { ...player, score: 0 }]));
  next.usedQuestionIds = state.usedQuestionIds;
  next.partyPets = state.partyPets;
  next.coralChorus = state.coralChorus;
  next.stainedGlass = state.stainedGlass;
  next.lastActive = Date.now();
  return next;
}

export function chooseQuestions(all: QuestionRecord[], usedIds: string[], count = DEFAULT_QUESTION_COUNT, random = Math.random) {
  let available = all.filter((question) => !usedIds.includes(question.id));
  if (available.length < count) available = [...all];
  for (let index = available.length - 1; index > 0; index -= 1) {
    const swap = Math.floor(random() * (index + 1));
    [available[index], available[swap]] = [available[swap], available[index]];
  }
  return available.slice(0, count).map((question) => question.id);
}

export function closestPrompt(question: QuestionRecord) {
  const scoreLabel = question.scoreType === "critics" ? "Critic score" : "Audience score";
  const movie = question.prompt.match(/ for (.+)\?$/)?.[1]?.replace(/[“”"]/g, "");
  return movie ? `${scoreLabel}; ${movie}` : `${scoreLabel}; ${question.prompt.replace(/\?$/, "")}`;
}

export function scoreClosestAttempts(attempts: AnswerAttempt[], targetValue: number) {
  const eligible = attempts.filter((attempt) => !attempt.skipped && Number.isInteger(attempt.guess));
  const smallestDistance = eligible.length
    ? Math.min(...eligible.map((attempt) => Math.abs(attempt.guess! - targetValue)))
    : null;
  return attempts.map((attempt) => {
    if (attempt.skipped || !Number.isInteger(attempt.guess)) {
      return { ...attempt, distance: undefined, correct: false };
    }
    const distance = Math.abs(attempt.guess! - targetValue);
    return { ...attempt, distance, correct: distance === smallestDistance };
  });
}

export function scoreWithoutGoingOver(attempts: AnswerAttempt[], targetValue: number) {
  const eligible = attempts.filter((attempt) => !attempt.skipped && Number.isSafeInteger(attempt.guess) && attempt.guess! <= targetValue);
  const winningGuess = eligible.length ? Math.max(...eligible.map((attempt) => attempt.guess!)) : null;
  return attempts.map((attempt) => {
    if (attempt.skipped || !Number.isSafeInteger(attempt.guess)) {
      return { ...attempt, distance: undefined, correct: false };
    }
    const distance = Math.abs(targetValue - attempt.guess!);
    return { ...attempt, distance, correct: winningGuess !== null && attempt.guess === winningGuess };
  });
}

export function publicState(state: GameState, questionsById: Map<string, QuestionRecord>, viewerId?: string, viewerIsAdmin = false, viewerIsDisplay = false) {
  const canObserveHoldings = canViewObserverHoldings(state, viewerId, viewerIsAdmin, viewerIsDisplay);
  const holdings = observerHoldings(state, viewerId, viewerIsAdmin, viewerIsDisplay);
  const isSpectator = Boolean(viewerId && state.players[viewerId]?.sittingOut);
  if (isSpectator) viewerId = undefined;
  const currentId = state.questionIds[state.questionNumber - 1];
  const question = currentId ? questionsById.get(currentId) : undefined;
  const closest = state.gameMode === "closest" && state.category === "rotten_tomatoes";
  const withoutOver = state.gameMode === "without_over" && state.category === "closest_without_over";
  const numerical = closest || withoutOver;
  const publicAttempts = viewerIsDisplay && state.phase === "question"
    ? state.attempts.map(({ playerId, playerName, answeredAt }) => ({ playerId, playerName, answeredAt }))
    : numerical && state.phase === "question"
      ? state.attempts.map(({ guess: _guess, distance: _distance, skipped: _skipped, ...attempt }) => attempt)
      : state.attempts;
  const base = {
    type: "state" as const,
    partyNight: state.partyNight || null,
    savedRoom: state.savedRoom || null,
    roomCode: state.roomCode,
    isAdmin: viewerIsAdmin,
    isDisplay: viewerIsDisplay,
    isSpectator,
    observerHoldings: holdings,
    adminOnlyPostGameControls: false,
    gameType: state.gameType,
    phase: state.phase,
    players: participatingPlayers(state).map((player) => ({ ...player, isAdmin: player.id === state.adminId })),
    spectators: Object.values(state.players).filter((player) => player.sittingOut).map((player) => ({ ...player, isAdmin: player.id === state.adminId })),
    question: question ? {
      id: question.id,
      prompt: closest ? closestPrompt(question) : question.prompt,
      choices: numerical ? [] : question.choices,
      difficulty: question.difficulty,
      era: question.era,
      ...(withoutOver ? { answerUnit: question.answerUnit || "" } : {}),
      ...(state.phase === "result" && question.scoreValue !== undefined ? {
        reveal: `${question.scoreType === "critics" ? "Critic" : "Audience"} score: ${question.scoreValue}% (captured ${question.scoreCapturedAt}).`,
      } : {}),
      ...(state.phase === "result" && question.answerDetail ? { reveal: question.answerDetail } : {}),
    } : null,
    questionNumber: state.questionNumber,
    totalQuestions: state.questionCount,
    deadline: state.deadline,
    result: state.result,
    attempts: publicAttempts,
    category: state.category,
    categoryLabel: CATEGORY_LABELS[state.category],
    gameMode: state.gameMode,
    maxQuestions: [...questionsById.values()].filter((candidate) => candidate.category === state.category).length,
    partyPets: publicPartyPetsState(state.partyPets, viewerId, viewerIsDisplay),
    hexchain: publicHexchainState(state.hexchain),
    wordMatch: null as null | {
      targetScore: number;
      roundNumber: number;
      judgeId: string | null;
      judgeName: string;
      greenCard: string | null;
      hand: string[];
      hasSubmitted: boolean;
      submittedCount: number;
      eligibleCount: number;
      submissions: Array<{ id: string; card: string; playerId?: string; playerName?: string }>;
      winner: WordSubmission | null;
    },
    impostor: null as null | {
      targetScore: number;
      roundNumber: number;
      role: "impostor" | "insider" | null;
      topic: string | null;
      topicCategory: string | null;
      hasSubmitted: boolean;
      submittedCount: number;
      eligibleCount: number;
      currentCluePlayerId: string | null;
      currentCluePlayerName: string;
      turnOrder: Array<{ playerId: string; playerName: string; submitted: boolean }>;
      clues: ImpostorClue[];
      hasVoted: boolean;
      votedForId: string | null;
      voteCount: number;
      result: ImpostorResult | null;
    },
    liarsDice: null as null | {
      roundNumber: number;
      hand: number[];
      diceCounts: Record<string, number>;
      eligiblePlayerIds: string[];
      isPlaying: boolean;
      currentPlayerId: string | null;
      currentPlayerName: string;
      currentBid: DiceBid | null;
      bidHistory: DiceBid[];
      totalDice: number;
      result: LiarsDiceResult | null;
    },
    taboo: null as null | {
      teamMode: "random" | "manual";
      teams: Record<string, TabooTeam>;
      teamScores: Record<TabooTeam, number>;
      targetScore: number;
      roundSeconds: number;
      roundNumber: number;
      activeTeam: TabooTeam | null;
      viewerTeam: TabooTeam | null;
      clueGiverId: string | null;
      clueGiverName: string;
      card: { word: string; forbidden: string[] } | null;
      correctThisTurn: number;
      skippedThisTurn: number;
      taboosThisTurn: number;
      lastAction: "correct" | "skip" | "taboo" | null;
      result: TabooTurnResult | null;
    },
    codeNames: null as null | {
      teamMode: "random" | "manual";
      teams: Record<string, CodeNamesTeam>;
      spymasters: Record<CodeNamesTeam, string | null>;
      spymasterNames: Record<CodeNamesTeam, string>;
      board: Array<{ word: string; role: CodeNamesRole | null; revealed: boolean }>;
      activeTeam: CodeNamesTeam | null;
      startingTeam: CodeNamesTeam | null;
      viewerTeam: CodeNamesTeam | null;
      isSpymaster: boolean;
      clue: { word: string; count: number } | null;
      clueHistory: Array<{ team: CodeNamesTeam; word: string; count: number; turnNumber: number }>;
      guessesLeft: number;
      bonusGuessAvailable: boolean;
      turnNumber: number;
      remaining: Record<CodeNamesTeam, number>;
      winnerTeam: CodeNamesTeam | null;
      lastGuess: { index: number; role: CodeNamesRole; playerId: string; playerName: string } | null;
    },
    sequence: null as null | {
      teamCount: 2 | 3;
      teams: Record<string, SequenceTeam>;
      teamScores: Record<SequenceTeam, number>;
      targetSequences: number;
      board: Array<SequenceCard | null>;
      chips: Array<SequenceTeam | null>;
      locked: boolean[];
      hand: SequenceCard[];
      deadCards: boolean[];
      legalSpaces: number[][];
      playerOrder: string[];
      turnOrder: Array<{ playerId: string; playerName: string; team: SequenceTeam }>;
      currentPlayerId: string | null;
      currentPlayerName: string;
      viewerTeam: SequenceTeam | null;
      canAct: boolean;
      turnNumber: number;
      deckRemaining: number;
      winnerTeam: SequenceTeam | null;
      lastMove: SequenceState["lastMove"];
    },
    poker: null as null | {
      smallBlind: number;
      bigBlind: number;
      startingStack: number;
      seatOrder: string[];
      stacks: Record<string, number>;
      statuses: Record<string, PokerPlayerStatus>;
      dealerIndex: number;
      handNumber: number;
      board: PokerCard[];
      holeCards: PokerCard[];
      observerHands?: PokerPrivateHand[];
      street: PokerStreet;
      currentPlayerId: string | null;
      currentPlayerName: string;
      currentBet: number;
      streetBets: Record<string, number>;
      contributions: Record<string, number>;
      pot: number;
      callAmount: number;
      minimumRaiseTo: number;
      maximumRaiseTo: number;
      canAct: boolean;
      canCheck: boolean;
      canRaise: boolean;
      actions: PokerAction[];
      result: PokerState["result"];
      history: PokerPublicHandHistory[];
    },
    blackjack: null as null | {
      startingBankroll: number;
      defaultBet: number;
      roundNumber: number;
      deckRemaining: number;
      shuffleCount: number;
      reshuffledThisRound: boolean;
      showCardCount: boolean;
      cardCount: number | null;
      playerOrder: string[];
      stacks: Record<string, number>;
      bets: Record<string, number>;
      hasBet: Record<string, boolean>;
      hands: Record<string, BlackjackCard[][]>;
      handBets: Record<string, number[]>;
      totals: Record<string, number[]>;
      statuses: Record<string, BlackjackPlayerStatus[]>;
      fromSplit: Record<string, boolean[]>;
      insuranceBets: Record<string, number>;
      insuranceDecisions: Record<string, boolean>;
      insurancePayouts: Record<string, number>;
      dealerHand: Array<BlackjackCard | null>;
      dealerTotal: number;
      currentPlayerId: string | null;
      currentHandIndex: number;
      currentPlayerName: string;
      canAct: boolean;
      canBet: boolean;
      canInsure: boolean;
      insuranceOffer: number;
      canDouble: boolean;
      canSplit: boolean;
      bettingCount: number;
      eligibleCount: number;
      results: BlackjackResult[];
    },
    hearts: null as ReturnType<typeof heartsPublicView> | null,
    spades: null as ReturnType<typeof spadesPublicView> | null,
    uno: null as ReturnType<typeof unoPublicView> | null,
    monopolyDeal: null as null | {
      hand: MonopolyCard[];
      boards: Record<string, { bank: MonopolyCard[]; properties: Array<MonopolyPlacedProperty & { card: MonopolyCard }>; improvements: Array<MonopolyImprovement & { card: MonopolyCard }>; completeColors: MonopolyColor[] }>;
      playerOrder: string[];
      currentPlayerId: string | null;
      currentPlayerName: string;
      playsThisTurn: number;
      deckRemaining: number;
      discardCount: number;
      canAct: boolean;
      pendingAction: MonopolyPendingAction | null;
      pendingPayment: MonopolyPendingPayment | null;
      winnerId: string | null;
      lastAction: string;
      log: string[];
      stats: Record<string, MonopolyPlayerStats>;
      colors: typeof MONOPOLY_COLORS;
    },
    kingTokyo: null as null | {
      matchId: string | null;
      victoryTarget: number;
      startingLife: number;
      powerUpEnabled: boolean;
      halloweenEnabled: boolean;
      evenMoreWickedEnabled: boolean;
      wickednessMode: KingTokyoState["wickednessMode"];
      wickednessCatalog: KingTokyoWickednessTile[];
      wickednessAvailable: Record<3 | 6 | 10, KingTokyoWickednessTile[]>;
      selectedMonsters: Record<string, KingTokyoMonsterId>;
      monsters: Array<{ id: string; name: string; icon: string; art: string; color: string; blurb: string; strategy?: string }>;
      cardCatalog: KingTokyoCard[];
      costumeCatalog: KingTokyoCard[];
      evolutionCatalog: KingTokyoEvolution[];
      players: Record<string, Omit<KingTokyoPlayerState, "cards" | "evolutionHand" | "evolutions" | "wickednessTiles"> & { cards: KingTokyoCard[]; evolutionHand: KingTokyoEvolution[]; evolutions: KingTokyoEvolution[]; wickednessTiles: KingTokyoWickednessTile[]; evolutionHandCount: number; evolutionDeckRemaining: number; evolutionDiscardCount: number }>;
      playerOrder: string[];
      currentPlayerId: string | null;
      currentPlayerName: string;
      turnNumber: number;
      market: typeof KING_TOKYO_CARDS;
      deckRemaining: number;
      discardCount: number;
      dice: KingTokyoDie[];
      held: boolean[];
      turnRolls: KingTokyoState["turnRolls"];
      lastRollResult: KingTokyoState["lastRollResult"];
      lastCardPurchase: null | { id: number; turnNumber: number; playerId: string; energyPaid: number; card: KingTokyoCard };
      lastYieldDecision: KingTokyoState["lastYieldDecision"];
      rollNumber: number;
      maxRolls: number;
      pendingYieldIds: string[];
      pendingAttackerId: string | null;
      tokyoCityId: string | null;
      tokyoBayId: string | null;
      bayActive: boolean;
      winnerIds: string[];
      winReason: KingTokyoState["winReason"];
      stats: KingTokyoState["stats"];
      initiative: Record<string, number>;
      lastAction: string;
      log: string[];
      matchHistory: KingTokyoState["matchHistory"];
      recoveryAutosaves: KingTokyoRecoverySnapshotSummary[];
      recoverySlots: Array<KingTokyoRecoverySnapshotSummary | null>;
      canRoll: boolean;
      canResolve: boolean;
      canBuy: boolean;
      canYield: boolean;
      canUsePsychicProbe: boolean;
      canUseWings: boolean;
      canUseCheerleader: boolean;
      canUseWitch: boolean;
      canUseRobot: boolean;
      canUseClown: boolean;
      canStealCostume: boolean;
      canPassReaction: boolean;
      canPassOpportunist: boolean;
      opportunistMarketIndices: number[];
      healingRayTargetId: string | null;
      heartAllocations: string[];
      pendingMetamorphPlayerId: string | null;
      pendingDeathFromAbovePlayerId: string | null;
      pendingDeathFromAboveTargets: string[];
      topDeckCard: KingTokyoCard | null;
      pendingEvolutionChoices: KingTokyoEvolution[];
      pendingEvolutionPlayerIds: string[];
      initialEvolutionSelection: boolean;
      pendingCostumeChoices: KingTokyoCard[];
      pendingCostumePlayerIds: string[];
      initialCostumeSelection: boolean;
      pendingCostumeStealPlayerId: string | null;
      pendingCostumeStealTargets: string[];
      pendingWickednessChoices: KingTokyoWickednessTile[];
      pendingWickednessPlayerIds: string[];
      pendingFluxlingPlayerId: string | null;
    },
    familyFeud: null as null | {
      teams: Record<string, "a" | "b">;
      teamScores: Record<"a" | "b", number>;
      activeTeam: "a" | "b";
      canJudge: boolean;
      roundNumber: number;
      prompt: string;
      answers: Array<{ label: string; points: number | null; revealed: boolean }>;
      strikes: number;
      roundBank: number;
      multiplier: number;
      roundAwardedTo: "a" | "b" | null;
      skippedRounds: number;
      winnerTeam: "a" | "b" | null;
      sourceKind: "crowdsourced" | "game-show-archive" | null;
      sourceTitle: string;
      sourceUrl: string;
    },
    kartRace: null as KartRaceState | null,
    connectFour: null as ReturnType<typeof connectFourPublicView> | null,
    puzzleFighter: null as ReturnType<typeof puzzleFighterPublicView> | null,
    ticketToRide: null as ReturnType<typeof ticketToRidePublicView> | null,
    axisAllies: null as ReturnType<typeof axisAlliesPublicView> | null,
    stratego: null as ReturnType<typeof strategoPublicView> | null,
    modernArt: null as ReturnType<typeof modernArtPublicView> | null,
    marketWars: null as ReturnType<typeof marketPublicView> | null,
    catan: null as ReturnType<typeof catanPublicView> | null,
    coralChorus: null as ReturnType<typeof coralRoomPublicView> | null,
    stainedGlass: null as ReturnType<typeof glassRoomPublicView> | null,
  };
  if (state.gameType === "catan") return { ...base, question: null, totalQuestions: 0, maxQuestions: 0, catan: catanPublicView(state.catan, viewerId) };
  if (state.gameType === "coral_chorus") return { ...base, question: null, totalQuestions: 0, maxQuestions: 0, coralChorus: coralRoomPublicView(state.coralChorus, viewerId || "") };
  if (state.gameType === "stained_glass") return { ...base, question: null, totalQuestions: 0, maxQuestions: 0, stainedGlass: glassRoomPublicView(state.stainedGlass, viewerId || "", viewerIsAdmin) };
  if (state.gameType === "market_wars") return { ...base, question: null, marketWars: marketPublicView(state.marketWars, viewerId) };
  if (state.gameType === "modern_art") {
    return { ...base, question: null, questionNumber: state.modernArt.round, totalQuestions: 4, maxQuestions: 4,
      modernArt: modernArtPublicView(state.modernArt, viewerId) };
  }
  if (state.gameType === "stratego") {
    return {
      ...base,
      question: null,
      questionNumber: state.stratego.moveCount,
      totalQuestions: 0,
      maxQuestions: 0,
      stratego: strategoPublicView(state.stratego, viewerId),
    };
  }
  if (state.gameType === "axis_allies") {
    return {
      ...base,
      question: null,
      questionNumber: state.axisAllies.round,
      totalQuestions: 0,
      maxQuestions: 0,
      axisAllies: axisAlliesPublicView(state.axisAllies, viewerId),
    };
  }
  if (state.gameType === "ticket_to_ride") {
    return {
      ...base,
      question: null,
      questionNumber: state.ticketToRide.turnNumber,
      totalQuestions: 0,
      maxQuestions: 0,
      ticketToRide: ticketToRidePublicView(state.ticketToRide, viewerId),
    };
  }
  if (state.gameType === "connect_four") {
    return {
      ...base,
      question: null,
      questionNumber: state.connectFour.moveCount,
      totalQuestions: 42,
      maxQuestions: 42,
      connectFour: connectFourPublicView(state.connectFour, state.phase === "lobby" ? "lobby" : state.phase as "connect_four_playing" | "finished", viewerId,
        participatingPlayers(state).filter((player) => player.connected).map((player) => player.id)),
    };
  }
  if (state.gameType === "puzzle_fighter") {
    return {
      ...base,
      question: null,
      questionNumber: state.puzzleFighter.moveCount,
      totalQuestions: 0,
      maxQuestions: 0,
      puzzleFighter: puzzleFighterPublicView(state.puzzleFighter, state.phase === "lobby" ? "lobby" : state.phase as "puzzle_fighter_playing" | "finished", viewerId),
    };
  }
  if (state.gameType === "hexchain") {
    return {
      ...base,
      question: null,
      questionNumber: 0,
      totalQuestions: 0,
      maxQuestions: 0,
      deadline: state.hexchain.deadline,
    };
  }
  if (state.gameType === "family_feud") {
    const roundId = state.familyFeud.roundIds[state.familyFeud.roundNumber - 1];
    const round = FAMILY_FEUD_ROUNDS.find((candidate) => candidate.id === roundId);
    const showAll = state.phase === "feud_round_result" || state.phase === "finished";
    const viewerTeam = viewerId ? state.familyFeud.teams[viewerId] : undefined;
    const canJudge = Boolean(((viewerIsAdmin && isSpectator) || (viewerTeam && viewerTeam !== state.familyFeud.activeTeam)) && !showAll);
    return {
      ...base,
      question: null,
      questionNumber: state.familyFeud.roundNumber,
      totalQuestions: state.questionCount,
      maxQuestions: FAMILY_FEUD_ROUNDS.length,
      familyFeud: {
        teams: state.familyFeud.teams,
        teamScores: state.familyFeud.teamScores,
        activeTeam: state.familyFeud.activeTeam,
        canJudge,
        roundNumber: state.familyFeud.roundNumber,
        prompt: round?.prompt || "",
        answers: (round?.answers || []).map((answer, index) => {
          const revealed = state.familyFeud.revealedAnswerIndexes.includes(index);
          const visible = revealed || showAll || canJudge;
          return { label: visible ? answer.label : "", points: visible ? answer.points : null, revealed };
        }),
        strikes: state.familyFeud.strikes,
        roundBank: state.familyFeud.roundBank,
        multiplier: familyFeudMultiplier(state.familyFeud.roundNumber),
        roundAwardedTo: state.familyFeud.roundAwardedTo,
        skippedRounds: state.familyFeud.skippedRounds,
        winnerTeam: state.familyFeud.winnerTeam,
        sourceKind: round?.sourceKind || null,
        sourceTitle: round?.sourceTitle || "",
        sourceUrl: round?.sourceUrl || "",
      },
    };
  }
  if (state.gameType === "hearts") {
    return {
      ...base,
      question: null,
      questionNumber: state.hearts.roundNumber,
      totalQuestions: state.hearts.targetScore,
      maxQuestions: state.hearts.targetScore,
      hearts: heartsPublicView(state.hearts, state.phase === "lobby" ? "lobby" : state.phase as "hearts_passing" | "hearts_playing" | "hearts_round_result" | "finished", viewerId),
    };
  }
  if (state.gameType === "spades") {
    return {
      ...base,
      question: null,
      questionNumber: state.spades.roundNumber,
      totalQuestions: state.spades.targetScore,
      maxQuestions: state.spades.targetScore,
      spades: spadesPublicView(state.spades, state.phase === "lobby" ? "lobby" : state.phase as "spades_bidding" | "spades_playing" | "spades_round_result" | "finished", viewerId),
    };
  }
  if (state.gameType === "uno") {
    return {
      ...base,
      question: null,
      questionNumber: state.uno.roundNumber,
      totalQuestions: state.uno.targetScore,
      maxQuestions: state.uno.targetScore,
      uno: unoPublicView(state.uno, state.phase === "lobby" ? "lobby" : state.phase as "uno_playing" | "uno_round_result" | "finished", viewerId),
    };
  }
  if (state.gameType === "turbo_rally") {
    return {
      ...base,
      question: null,
      questionNumber: 0,
      totalQuestions: state.kartRace.laps,
      maxQuestions: 5,
      kartRace: state.kartRace,
    };
  }
  if (state.gameType === "king_of_tokyo" || state.gameType === KING_COURT_GAME_TYPE) {
    const game = state.kingTokyo;
    const isKingCourt = state.gameType === KING_COURT_GAME_TYPE;
    const themedCard = (card: KingTokyoCard) => isKingCourt ? kingCourtCard(card) : card;
    const themedEvolution = (card: KingTokyoEvolution) => isKingCourt ? kingCourtEvolution(card) : card;
    const themedWickedness = (tile: KingTokyoWickednessTile) => isKingCourt ? kingCourtWickedness(tile) : tile;
    const themedText = (text: string) => isKingCourt ? courtifyText(text) : text;
    const currentPlayerId = game.playerOrder[game.currentPlayerIndex] || null;
    const viewer = viewerId ? game.players[viewerId] : undefined;
    const mimicTargetValid = (playerId: string) => {
      const player = game.players[playerId];
      if (!player?.mimicTargetKey) return false;
      if (player.mimicTargetPlayerId && player.mimicTargetCardId) {
        const owner = game.players[player.mimicTargetPlayerId];
        const index = owner?.cardIds?.indexOf(player.mimicTargetCardId) ?? -1;
        return Boolean(owner?.alive && player.mimicTargetPlayerId !== playerId && index >= 0 && owner.cards[index] === player.mimicTargetKey);
      }
      return game.playerOrder.some((otherId) => otherId !== playerId && game.players[otherId]?.alive && game.players[otherId].cards.includes(player.mimicTargetKey!));
    };
    const fluxlingTargetValid = (playerId: string) => {
      const player = game.players[playerId];
      if (!player?.wickednessTiles.includes("fluxling") || !player.fluxlingTargetKey || !player.fluxlingTargetPlayerId || !player.fluxlingTargetCardId) return false;
      const owner = game.players[player.fluxlingTargetPlayerId];
      const index = owner?.cardIds?.indexOf(player.fluxlingTargetCardId) ?? -1;
      return Boolean(owner?.alive && index >= 0 && owner.cards[index] === player.fluxlingTargetKey);
    };
    const viewerHasCard = (key: string) => Boolean(viewerId && viewer && (viewer.cards.includes(key) || (
      viewer.cards.includes("mimic")
      && viewer.mimicTargetKey === key
      && mimicTargetValid(viewerId)
    ) || (viewer.fluxlingTargetKey === key && fluxlingTargetValid(viewerId))));
    const defaultHeartAllocations = () => {
      if (!currentPlayerId) return [];
      const player = game.players[currentPlayerId];
      const heartCount = game.dice.filter((die) => die === "heart").length * (player?.wickednessTiles.includes("skybeam") ? 2 : 1);
      if (!player || game.tokyoCityId === currentPlayerId || game.tokyoBayId === currentPlayerId) return Array(heartCount).fill("");
      let poison = player.poison;
      let shrink = player.shrink;
      let missingLife = Math.max(0, player.maxHealth - player.health);
      return Array.from({ length: heartCount }, () => {
        if (poison > 0) { poison -= 1; return "poison"; }
        if (shrink > 0) { shrink -= 1; return "shrink"; }
        if (missingLife > 0) { missingLife -= 1; return "self"; }
        return "";
      });
    };
    return {
      ...base,
      question: null,
      questionNumber: game.turnNumber,
      totalQuestions: game.victoryTarget,
      maxQuestions: game.victoryTarget,
      kingTokyo: {
        matchId: state.gameSessionId,
        victoryTarget: game.victoryTarget,
        startingLife: game.startingLife,
        powerUpEnabled: game.powerUpEnabled,
        halloweenEnabled: game.halloweenEnabled,
        evenMoreWickedEnabled: game.evenMoreWickedEnabled,
        wickednessMode: game.wickednessMode,
        wickednessCatalog: KING_TOKYO_WICKEDNESS_TILES.map(themedWickedness),
        wickednessAvailable: {
          3: game.wickednessAvailable[3].map((key) => KING_TOKYO_WICKEDNESS_MAP[key]).filter(Boolean).map(themedWickedness),
          6: game.wickednessAvailable[6].map((key) => KING_TOKYO_WICKEDNESS_MAP[key]).filter(Boolean).map(themedWickedness),
          10: game.wickednessAvailable[10].map((key) => KING_TOKYO_WICKEDNESS_MAP[key]).filter(Boolean).map(themedWickedness),
        },
        selectedMonsters: game.selectedMonsters,
        monsters: isKingCourt ? [...KING_COURT_TEAMS] : [...KING_TOKYO_MONSTERS],
        cardCatalog: (game.halloweenEnabled ? KING_TOKYO_ALL_CARDS : KING_TOKYO_CARDS).map(themedCard),
        costumeCatalog: isKingCourt ? [] : KING_TOKYO_COSTUMES,
        evolutionCatalog: KING_TOKYO_EVOLUTIONS.filter((card) => !isKingCourt || KING_COURT_TEAMS.some((team) => team.id === card.monsterId)).map(themedEvolution),
        players: Object.fromEntries(game.playerOrder.map((playerId) => [playerId, {
          ...game.players[playerId],
          evolutionDeck: [],
          evolutionDiscard: [],
          mimicTargetKey: mimicTargetValid(playerId) ? game.players[playerId].mimicTargetKey : null,
          cards: (game.players[playerId]?.cards || []).map((key) => KING_TOKYO_CARD_MAP[key]).filter(Boolean).map(themedCard),
          evolutionHand: playerId === viewerId ? (game.players[playerId]?.evolutionHand || []).map((key) => KING_TOKYO_EVOLUTION_MAP[key]).filter(Boolean).map(themedEvolution) : [],
          evolutions: (game.players[playerId]?.evolutions || []).map((key) => KING_TOKYO_EVOLUTION_MAP[key]).filter(Boolean).map(themedEvolution),
          wickednessTiles: (game.players[playerId]?.wickednessTiles || []).map((key) => KING_TOKYO_WICKEDNESS_MAP[key]).filter(Boolean).map(themedWickedness),
          evolutionHandCount: game.players[playerId]?.evolutionHand?.length || 0,
          evolutionDeckRemaining: game.players[playerId]?.evolutionDeck?.length || 0,
          evolutionDiscardCount: game.players[playerId]?.evolutionDiscard?.length || 0,
        }])),
        playerOrder: game.playerOrder,
        currentPlayerId,
        currentPlayerName: state.players[currentPlayerId || ""]?.name || "",
        turnNumber: game.turnNumber,
        market: game.market.map((key) => KING_TOKYO_CARD_MAP[key]).filter(Boolean).map(themedCard),
        deckRemaining: game.deck.length,
        discardCount: game.discard.length,
        dice: game.dice,
        held: game.held,
        turnRolls: game.turnRolls || [],
        lastRollResult: game.lastRollResult || null,
        lastCardPurchase: game.lastCardPurchase && KING_TOKYO_CARD_MAP[game.lastCardPurchase.cardKey]
          ? {
              id: game.lastCardPurchase.id,
              turnNumber: game.lastCardPurchase.turnNumber,
              playerId: game.lastCardPurchase.playerId,
              energyPaid: game.lastCardPurchase.energyPaid,
              card: themedCard(KING_TOKYO_CARD_MAP[game.lastCardPurchase.cardKey]),
            }
          : null,
        lastYieldDecision: game.lastYieldDecision || null,
        rollNumber: game.rollNumber,
        maxRolls: game.maxRolls,
        pendingYieldIds: game.pendingYieldIds,
        pendingAttackerId: game.pendingAttackerId,
        tokyoCityId: game.tokyoCityId,
        tokyoBayId: game.tokyoBayId,
        bayActive: game.playerOrder.filter((id) => game.players[id]?.alive).length >= 5,
        winnerIds: game.winnerIds,
        winReason: game.winReason || null,
        stats: game.stats || {},
        initiative: game.initiative,
        lastAction: themedText(game.lastAction),
        log: game.log.map(themedText),
        matchHistory: (game.matchHistory || []).map((match) => ({
          ...match,
          winnerIds: [...match.winnerIds],
          players: match.players.map((player) => ({ ...player, stats: { ...player.stats } })),
          log: match.log.map(themedText),
        })),
        recoveryAutosaves: (game.recoveryAutosaves || []).map(({ stateJson: _stateJson, ...snapshot }) => snapshot),
        recoverySlots: (game.recoverySlots || []).map((snapshot) => snapshot ? (({ stateJson: _stateJson, ...summary }) => summary)(snapshot) : null),
        canRoll: state.phase === "tokyo_roll" && currentPlayerId === viewerId && game.rollNumber < game.maxRolls && !game.initialEvolutionSelection && !game.initialCostumeSelection && !Object.keys(game.pendingEvolutionChoices).length && !Object.keys(game.pendingCostumeChoices).length && !Object.keys(game.pendingWickednessLevels).length && !game.pendingFluxlingPlayerId && !(game.psychicProbeQueue?.length) && !(game.pendingWingsQueue?.length) && !(game.pendingRobotQueue?.length) && !(game.pendingCheerleaderQueue?.length) && !(game.pendingWitchQueue?.length),
        canResolve: state.phase === "tokyo_roll" && currentPlayerId === viewerId && game.rollNumber > 0 && !game.initialEvolutionSelection && !game.initialCostumeSelection && !Object.keys(game.pendingEvolutionChoices).length && !Object.keys(game.pendingCostumeChoices).length && !Object.keys(game.pendingWickednessLevels).length && !game.pendingFluxlingPlayerId && !(game.psychicProbeQueue?.length) && !(game.pendingWingsQueue?.length) && !(game.pendingRobotQueue?.length) && !(game.pendingCheerleaderQueue?.length) && !(game.pendingWitchQueue?.length),
        canBuy: state.phase === "tokyo_buy" && currentPlayerId === viewerId && !Object.keys(game.pendingEvolutionChoices).length && !Object.keys(game.pendingWickednessLevels).length && !game.pendingFluxlingPlayerId && !(game.opportunistQueue?.length) && !game.pendingDeathFromAbovePlayerId && !game.pendingMetamorphPlayerId,
        canYield: state.phase === "tokyo_yield" && !Object.keys(game.pendingEvolutionChoices).length && !Object.keys(game.pendingWickednessLevels).length && !game.pendingFluxlingPlayerId && Boolean(viewerId && game.pendingYieldIds.includes(viewerId) && game.yieldDecisions[viewerId] === undefined),
        canUsePsychicProbe: state.phase === "tokyo_roll"
          && Boolean(viewerId && viewer?.alive && currentPlayerId && currentPlayerId !== viewerId && game.dice.length && viewerHasCard("psychic_probe") && game.psychicProbeQueue?.[0] === viewerId)
          && (game.psychicProbeUsesThisTurn?.[viewerId || ""] || 0) < (viewer?.cards.filter((key) => key === "psychic_probe").length || 0)
            + (viewer?.mimicTargetKey === "psychic_probe" && mimicTargetValid(viewerId || "") ? 1 : 0)
            + (viewer?.fluxlingTargetKey === "psychic_probe" && fluxlingTargetValid(viewerId || "") ? 1 : 0),
        canUseWings: ["tokyo_roll", "tokyo_buy"].includes(state.phase)
          && Boolean(viewerId && viewer?.alive && viewer.energy >= 2 && currentPlayerId && currentPlayerId !== viewerId && viewerHasCard("wings"))
          && (game.pendingWingsQueue?.length ? game.pendingWingsQueue[0] === viewerId : state.phase === "tokyo_roll")
          && (game.wingsProtectedTurns?.[viewerId || ""] || 0) !== game.turnNumber,
        canUseCheerleader: Boolean(viewerId && game.pendingCheerleaderQueue?.[0] === viewerId),
        canUseWitch: Boolean(viewerId && game.pendingWitchQueue?.[0] === viewerId),
        canUseRobot: Boolean(viewerId && game.pendingRobotQueue?.[0] === viewerId),
        canUseClown: Boolean(viewerId && currentPlayerId === viewerId && state.phase === "tokyo_roll" && viewerHasCard("clown_costume") && game.usedClownTurn !== game.turnNumber && KING_TOKYO_DIE_FACES.every((face) => game.dice.includes(face))),
        canStealCostume: Boolean(viewerId && currentPlayerId === viewerId && state.phase === "tokyo_costume" && game.pendingCostumeStealPlayerId === viewerId),
        canPassReaction: Boolean(viewerId && (game.psychicProbeQueue?.[0] === viewerId || game.pendingWingsQueue?.[0] === viewerId || game.pendingCheerleaderQueue?.[0] === viewerId || game.pendingWitchQueue?.[0] === viewerId || game.pendingRobotQueue?.[0] === viewerId)),
        canPassOpportunist: Boolean(viewerId && game.opportunistQueue?.[0] === viewerId),
        opportunistMarketIndices: state.phase === "tokyo_buy" && viewerHasCard("opportunist") && game.opportunistQueue?.[0] === viewerId
          ? game.market.flatMap((_key, index) => game.marketCardIds?.[index] && (game.opportunistRevealedCardIds || []).includes(game.marketCardIds[index]) ? [index] : [])
          : [],
        healingRayTargetId: game.healingRayTargetId || null,
        heartAllocations: game.heartAllocations?.length ? game.heartAllocations : defaultHeartAllocations(),
        pendingMetamorphPlayerId: game.pendingMetamorphPlayerId || null,
        pendingDeathFromAbovePlayerId: game.pendingDeathFromAbovePlayerId || null,
        pendingDeathFromAboveTargets: game.pendingDeathFromAboveTargets || [],
        topDeckCard: state.phase === "tokyo_buy" && currentPlayerId === viewerId && viewerHasCard("made_in_a_lab") && game.deck[game.deck.length - 1]
          ? themedCard(KING_TOKYO_CARD_MAP[game.deck[game.deck.length - 1]])
          : null,
        pendingEvolutionChoices: viewerId ? (game.pendingEvolutionChoices[viewerId] || []).map((key) => KING_TOKYO_EVOLUTION_MAP[key]).filter(Boolean).map(themedEvolution) : [],
        pendingEvolutionPlayerIds: Object.keys(game.pendingEvolutionChoices),
        initialEvolutionSelection: game.initialEvolutionSelection,
        pendingCostumeChoices: viewerId ? (game.pendingCostumeChoices[viewerId] || []).map((key) => KING_TOKYO_CARD_MAP[key]).filter(Boolean) : [],
        pendingCostumePlayerIds: Object.keys(game.pendingCostumeChoices),
        initialCostumeSelection: game.initialCostumeSelection,
        pendingCostumeStealPlayerId: game.pendingCostumeStealPlayerId || null,
        pendingCostumeStealTargets: game.pendingCostumeStealTargets || [],
        pendingWickednessChoices: viewerId && game.pendingWickednessLevels[viewerId]?.[0] ? game.wickednessAvailable[game.pendingWickednessLevels[viewerId][0]].map((key) => KING_TOKYO_WICKEDNESS_MAP[key]).filter(Boolean).map(themedWickedness) : [],
        pendingWickednessPlayerIds: Object.keys(game.pendingWickednessLevels),
        pendingFluxlingPlayerId: game.pendingFluxlingPlayerId,
      },
    };
  }
  if (state.gameType === "monopoly_deal") {
    const game = state.monopolyDeal;
    const currentPlayerId = game.playerOrder[game.currentPlayerIndex] || null;
    const boards = Object.fromEntries(game.playerOrder.map((playerId) => [playerId, {
      bank: (game.banks[playerId] || []).map((cardId) => game.cards[cardId]).filter(Boolean),
      properties: (game.properties[playerId] || []).map((placed) => ({ ...placed, card: game.cards[placed.cardId] })).filter((placed) => placed.card),
      improvements: (game.improvements[playerId] || []).map((placed) => ({ ...placed, card: game.cards[placed.cardId] })).filter((placed) => placed.card),
      completeColors: monopolyCompleteColors(game.properties[playerId] || []),
    }]));
    return {
      ...base,
      question: null,
      questionNumber: 0,
      totalQuestions: 3,
      maxQuestions: 3,
      monopolyDeal: {
        hand: viewerId ? (game.hands[viewerId] || []).map((cardId) => game.cards[cardId]).filter(Boolean) : [],
        boards,
        playerOrder: game.playerOrder,
        currentPlayerId,
        currentPlayerName: state.players[currentPlayerId || ""]?.name || "",
        playsThisTurn: game.playsThisTurn,
        deckRemaining: game.deck.length,
        discardCount: game.discard.length,
        canAct: state.phase === "monopoly_turn" && currentPlayerId === viewerId && !game.pendingAction && !game.pendingPayment,
        pendingAction: game.pendingAction,
        pendingPayment: game.pendingPayment,
        winnerId: game.winnerId,
        lastAction: game.lastAction,
        log: (game.log || []).slice(-40),
        stats: game.stats || {},
        colors: MONOPOLY_COLORS,
      },
    };
  }
  if (state.gameType === "blackjack") {
    const revealDealer = state.phase === "blackjack_result" || state.phase === "finished";
    const hiddenHoleCount = !revealDealer && state.blackjack.dealerHand[1] && state.blackjack.dealerHoleShuffleCount === state.blackjack.shuffleCount
      ? hiLoCardValue(state.blackjack.dealerHand[1])
      : 0;
    const visibleDealerHand: Array<BlackjackCard | null> = revealDealer
      ? state.blackjack.dealerHand
      : state.blackjack.dealerHand.map((card, index) => index === 0 ? card : null);
    return {
      ...base,
      question: null,
      questionNumber: state.blackjack.roundNumber,
      totalQuestions: 0,
      maxQuestions: 0,
      blackjack: {
        startingBankroll: state.blackjack.startingBankroll,
        defaultBet: state.blackjack.defaultBet,
        roundNumber: state.blackjack.roundNumber,
        deckRemaining: state.blackjack.deck.length,
        shuffleCount: state.blackjack.shuffleCount,
        reshuffledThisRound: state.blackjack.reshuffledThisRound,
        showCardCount: state.blackjack.showCardCount,
        cardCount: state.blackjack.showCardCount ? state.blackjack.runningCount - hiddenHoleCount : null,
        playerOrder: state.blackjack.playerOrder,
        stacks: state.blackjack.stacks,
        bets: state.blackjack.bets,
        hasBet: state.blackjack.hasBet,
        hands: state.blackjack.hands,
        handBets: state.blackjack.handBets,
        totals: Object.fromEntries(Object.entries(state.blackjack.hands).map(([playerId, hands]) => [playerId, hands.map((hand) => blackjackHandValue(hand).total)])),
        statuses: state.blackjack.statuses,
        fromSplit: state.blackjack.fromSplit,
        insuranceBets: state.blackjack.insuranceBets,
        insuranceDecisions: state.blackjack.insuranceDecisions,
        insurancePayouts: state.blackjack.insurancePayouts,
        dealerHand: visibleDealerHand,
        dealerTotal: blackjackHandValue(visibleDealerHand.filter((card): card is BlackjackCard => Boolean(card))).total,
        currentPlayerId: state.blackjack.currentPlayerId,
        currentHandIndex: state.blackjack.currentHandIndex,
        currentPlayerName: state.players[state.blackjack.currentPlayerId || ""]?.name || "",
        canAct: state.phase === "blackjack_playing" && state.blackjack.currentPlayerId === viewerId,
        canBet: state.phase === "blackjack_betting" && Boolean(viewerId && state.blackjack.playerOrder.includes(viewerId) && !state.blackjack.hasBet[viewerId]),
        canInsure: state.phase === "blackjack_insurance" && Boolean(viewerId && state.blackjack.playerOrder.includes(viewerId) && !state.blackjack.insuranceDecisions[viewerId]),
        insuranceOffer: viewerId ? Math.min(Math.floor((state.blackjack.bets[viewerId] || 0) / 2), state.blackjack.stacks[viewerId] || 0) : 0,
        canDouble: Boolean(viewerId && state.blackjack.currentPlayerId === viewerId && state.blackjack.hands[viewerId]?.[state.blackjack.currentHandIndex]?.length === 2 && (state.blackjack.stacks[viewerId] || 0) >= (state.blackjack.handBets[viewerId]?.[state.blackjack.currentHandIndex] || 0)),
        canSplit: Boolean(viewerId && state.blackjack.currentPlayerId === viewerId && canSplitBlackjackPair(state.blackjack.hands[viewerId]?.[state.blackjack.currentHandIndex] || []) && (state.blackjack.stacks[viewerId] || 0) >= (state.blackjack.handBets[viewerId]?.[state.blackjack.currentHandIndex] || 0) && (state.blackjack.hands[viewerId]?.length || 0) < 4),
        bettingCount: state.blackjack.playerOrder.filter((playerId) => state.blackjack.hasBet[playerId]).length,
        eligibleCount: state.blackjack.playerOrder.length,
        results: revealDealer ? state.blackjack.results : [],
      },
    };
  }
  if (state.gameType === "holdem") {
    const viewerStack = viewerId ? state.poker.stacks[viewerId] || 0 : 0;
    const viewerBet = viewerId ? state.poker.streetBets[viewerId] || 0 : 0;
    const callAmount = Math.min(viewerStack, Math.max(0, state.poker.currentBet - viewerBet));
    const canAct = state.phase === "poker_betting" && state.poker.currentPlayerId === viewerId && state.poker.statuses[viewerId || ""] === "active";
    return {
      ...base,
      question: null,
      questionNumber: state.poker.handNumber,
      totalQuestions: 0,
      maxQuestions: 0,
      poker: {
        smallBlind: state.poker.smallBlind,
        bigBlind: state.poker.bigBlind,
        startingStack: state.poker.startingStack,
        seatOrder: state.poker.seatOrder,
        stacks: state.poker.stacks,
        statuses: state.poker.statuses,
        dealerIndex: state.poker.dealerIndex,
        handNumber: state.poker.handNumber,
        board: state.poker.board,
        holeCards: viewerId ? state.poker.holeCards[viewerId] || [] : [],
        ...(canObserveHoldings ? { observerHands: snapshotPokerPrivateHands(state.poker, state.players) } : {}),
        street: state.poker.street,
        currentPlayerId: state.poker.currentPlayerId,
        currentPlayerName: state.players[state.poker.currentPlayerId || ""]?.name || "",
        currentBet: state.poker.currentBet,
        streetBets: state.poker.streetBets,
        contributions: state.poker.contributions,
        pot: Object.values(state.poker.contributions).reduce((total, amount) => total + amount, 0),
        callAmount,
        minimumRaiseTo: Math.min(viewerBet + viewerStack, state.poker.currentBet + state.poker.minimumRaise),
        maximumRaiseTo: viewerBet + viewerStack,
        canAct,
        canCheck: callAmount === 0,
        canRaise: canAct && !state.poker.acted[viewerId || ""] && viewerBet + viewerStack > state.poker.currentBet,
        actions: state.poker.actions,
        result: state.phase === "poker_result" || state.phase === "finished" ? state.poker.result : null,
        history: pokerHistoryPublicView(state.poker, state.players, canObserveHoldings, state.phase === "poker_result" || state.phase === "finished"),
      },
    };
  }
  if (state.gameType === "liars_dice") {
    const resultVisible = state.phase === "dice_result" || state.phase === "finished";
    return {
      ...base,
      question: null,
      questionNumber: state.liarsDice.roundNumber,
      totalQuestions: 5,
      maxQuestions: 5,
      liarsDice: {
        roundNumber: state.liarsDice.roundNumber,
        hand: viewerId ? state.liarsDice.hands[viewerId] || [] : [],
        diceCounts: state.liarsDice.diceCounts,
        eligiblePlayerIds: state.liarsDice.eligiblePlayerIds,
        isPlaying: Boolean(viewerId && state.liarsDice.eligiblePlayerIds.includes(viewerId)),
        currentPlayerId: state.liarsDice.currentPlayerId,
        currentPlayerName: state.players[state.liarsDice.currentPlayerId || ""]?.name || "",
        currentBid: state.liarsDice.currentBid,
        bidHistory: state.liarsDice.bidHistory,
        totalDice: state.liarsDice.eligiblePlayerIds.reduce((total, id) => total + (state.liarsDice.diceCounts[id] || 0), 0),
        result: resultVisible ? state.liarsDice.result : null,
      },
    };
  }
  if (state.gameType === "impostor") {
    const resultVisible = state.phase === "impostor_result" || state.phase === "finished";
    const isPlaying = Boolean(viewerId && state.impostor.eligiblePlayerIds.includes(viewerId));
    const isImpostor = isPlaying && viewerId === state.impostor.impostorId;
    const viewerVote = viewerId ? state.impostor.votes.find((vote) => vote.voterId === viewerId) : undefined;
    const eligibleCount = state.impostor.eligiblePlayerIds.filter((id) => state.players[id]?.connected).length;
    return {
      ...base,
      question: null,
      questionNumber: state.impostor.roundNumber,
      totalQuestions: state.impostor.targetScore,
      maxQuestions: 10,
      impostor: {
        targetScore: state.impostor.targetScore,
        roundNumber: state.impostor.roundNumber,
        role: isPlaying ? (isImpostor ? "impostor" : "insider") : null,
        topic: resultVisible || (isPlaying && !isImpostor) ? state.impostor.topic : null,
        topicCategory: state.impostor.topicCategory,
        hasSubmitted: viewerId ? state.impostor.clues.some((clue) => clue.playerId === viewerId) : false,
        submittedCount: state.impostor.clues.length,
        eligibleCount,
        currentCluePlayerId: state.impostor.clueOrder[state.impostor.currentClueIndex] || null,
        currentCluePlayerName: state.players[state.impostor.clueOrder[state.impostor.currentClueIndex]]?.name || "",
        turnOrder: state.impostor.clueOrder.map((playerId) => ({
          playerId,
          playerName: state.players[playerId]?.name || "Player",
          submitted: state.impostor.clues.some((clue) => clue.playerId === playerId),
        })),
        clues: state.impostor.clues,
        hasVoted: Boolean(viewerVote),
        votedForId: viewerVote?.targetId || null,
        voteCount: state.impostor.votes.length,
        result: resultVisible ? state.impostor.result : null,
      },
    };
  }
  if (state.gameType === "taboo") {
    const viewerTeam = viewerId ? state.taboo.teams[viewerId] || null : null;
    const canSeeCard = state.phase === "taboo_result"
      || state.phase === "finished"
      || Boolean(viewerId && (viewerId === state.taboo.clueGiverId || (viewerTeam && viewerTeam !== state.taboo.activeTeam)));
    const card = state.taboo.currentCardIndex === null ? null : TABOO_CARDS[state.taboo.currentCardIndex] || null;
    return {
      ...base,
      question: null,
      questionNumber: state.taboo.roundNumber,
      totalQuestions: state.taboo.targetScore,
      maxQuestions: 30,
      taboo: {
        teamMode: state.taboo.teamMode,
        teams: state.taboo.teams,
        teamScores: state.taboo.teamScores,
        targetScore: state.taboo.targetScore,
        roundSeconds: state.taboo.roundSeconds,
        roundNumber: state.taboo.roundNumber,
        activeTeam: state.taboo.activeTeam,
        viewerTeam,
        clueGiverId: state.taboo.clueGiverId,
        clueGiverName: state.players[state.taboo.clueGiverId || ""]?.name || "",
        card: canSeeCard && card ? card : null,
        correctThisTurn: state.taboo.correctThisTurn,
        skippedThisTurn: state.taboo.skippedThisTurn,
        taboosThisTurn: state.taboo.taboosThisTurn,
        lastAction: state.taboo.lastAction,
        result: state.phase === "taboo_result" || state.phase === "finished" ? state.taboo.result : null,
      },
    };
  }
  if (state.gameType === "codenames") {
    const viewerTeam = viewerId ? state.codeNames.teams[viewerId] || null : null;
    const isSpymaster = Boolean(viewerId && (state.codeNames.spymasters.red === viewerId || state.codeNames.spymasters.blue === viewerId));
    const revealAll = state.phase === "finished";
    return {
      ...base,
      question: null,
      questionNumber: state.codeNames.turnNumber,
      totalQuestions: 25,
      maxQuestions: 25,
      codeNames: {
        teamMode: state.codeNames.teamMode,
        teams: state.codeNames.teams,
        spymasters: state.codeNames.spymasters,
        spymasterNames: {
          red: state.players[state.codeNames.spymasters.red || ""]?.name || "",
          blue: state.players[state.codeNames.spymasters.blue || ""]?.name || "",
        },
        board: state.codeNames.board.map((card) => ({
          word: card.word,
          revealed: card.revealed,
          role: card.revealed || isSpymaster || revealAll ? card.role : null,
        })),
        activeTeam: state.codeNames.activeTeam,
        startingTeam: state.codeNames.startingTeam,
        viewerTeam,
        isSpymaster,
        clue: state.codeNames.clue,
        clueHistory: state.codeNames.clueHistory,
        guessesLeft: state.codeNames.guessesLeft,
        bonusGuessAvailable: state.codeNames.bonusGuessAvailable,
        turnNumber: state.codeNames.turnNumber,
        remaining: {
          red: state.codeNames.board.filter((card) => card.role === "red" && !card.revealed).length,
          blue: state.codeNames.board.filter((card) => card.role === "blue" && !card.revealed).length,
        },
        winnerTeam: state.codeNames.winnerTeam,
        lastGuess: state.codeNames.lastGuess,
      },
    };
  }
  if (state.gameType === "sequence") {
    const currentPlayerId = state.sequence.playerOrder[state.sequence.currentPlayerIndex] || null;
    const viewerTeam = viewerId ? state.sequence.teams[viewerId] || null : null;
    const hand = viewerId ? state.sequence.hands[viewerId] || [] : [];
    const connectedPlayerIds = state.sequence.playerOrder.filter((playerId) => state.players[playerId]?.connected);
    const turnOrder = sequenceUpcomingTurns(state.sequence.playerOrder, state.sequence.teams, state.sequence.teamCount, currentPlayerId, state.sequence.teamTurnIndices, connectedPlayerIds).map((playerId) => ({
      playerId,
      playerName: state.players[playerId]?.name || "Player",
      team: state.sequence.teams[playerId],
    }));
    return {
      ...base,
      question: null,
      questionNumber: state.sequence.turnNumber,
      totalQuestions: state.sequence.targetSequences,
      maxQuestions: state.sequence.targetSequences,
      sequence: {
        teamCount: state.sequence.teamCount,
        teams: state.sequence.teams,
        teamScores: state.sequence.teamScores,
        targetSequences: state.sequence.targetSequences,
        board: state.sequence.board,
        chips: state.sequence.chips,
        locked: state.sequence.locked,
        hand,
        deadCards: hand.map((card) => isDeadSequenceCard(card, state.sequence.board, state.sequence.chips)),
        legalSpaces: viewerTeam ? hand.map((card) => legalSequenceSpaces(card, viewerTeam, state.sequence.board, state.sequence.chips, state.sequence.locked)) : [],
        playerOrder: state.sequence.playerOrder,
        turnOrder,
        currentPlayerId,
        currentPlayerName: state.players[currentPlayerId || ""]?.name || "",
        viewerTeam,
        canAct: state.phase === "sequence_turn" && currentPlayerId === viewerId,
        turnNumber: state.sequence.turnNumber,
        deckRemaining: state.sequence.deck.length,
        winnerTeam: state.sequence.winnerTeam,
        lastMove: state.sequence.lastMove,
      },
    };
  }
  if (state.gameType !== "word_match") return base;
  const judge = state.wordMatch.judgeId ? state.players[state.wordMatch.judgeId] : null;
  const winner = state.wordMatch.winner;
  return {
    ...base,
    question: null,
    questionNumber: state.wordMatch.roundNumber,
    totalQuestions: state.wordMatch.targetScore,
    maxQuestions: 10,
    wordMatch: {
      targetScore: state.wordMatch.targetScore,
      roundNumber: state.wordMatch.roundNumber,
      judgeId: state.wordMatch.judgeId,
      judgeName: judge?.name || "",
      greenCard: state.wordMatch.greenCard,
      hand: viewerId ? state.wordMatch.hands[viewerId] || [] : [],
      hasSubmitted: viewerId ? state.wordMatch.submissions.some((submission) => submission.playerId === viewerId) : false,
      submittedCount: state.wordMatch.submissions.length,
      eligibleCount: participatingPlayers(state).filter((player) => player.connected && player.id !== state.wordMatch.judgeId).length,
      submissions: state.phase === "judging"
        ? state.wordMatch.submissions.map(({ id, card }) => ({ id, card }))
        : state.phase === "round_result"
          ? state.wordMatch.submissions.map(({ id, card, playerId, playerName }) => ({ id, card, playerId, playerName }))
          : [],
      winner: winner && state.phase === "round_result" ? winner : null,
    },
  };
}

export function isCategory(value: unknown): value is Category {
  return typeof value === "string" && CATEGORIES.includes(value as Category);
}

export function isGameType(value: unknown): value is GameType {
  return value === "trivia" || value === "word_match" || value === "impostor" || value === "closest" || value === "liars_dice" || value === "taboo" || value === "codenames" || value === "sequence" || value === "holdem" || value === "blackjack" || value === "hearts" || value === "spades" || value === "uno" || value === "monopoly_deal" || value === "king_of_tokyo" || value === KING_COURT_GAME_TYPE || value === "turbo_rally" || value === "party_pets" || value === "family_feud" || value === "hexchain" || value === "connect_four" || value === "puzzle_fighter" || value === "ticket_to_ride" || value === "axis_allies" || value === "stratego" || value === "modern_art" || value === "market_wars" || value === "catan" || value === "coral_chorus" || value === "stained_glass";
}

export function sanitizeOneWord(value: unknown) {
  if (typeof value !== "string") return null;
  const clue = value.trim();
  return /^[\p{L}\p{N}][\p{L}\p{N}'’-]{0,23}$/u.test(clue) ? clue : null;
}

export function isGameMode(value: unknown): value is GameMode {
  return typeof value === "string" && GAME_MODES.includes(value as GameMode);
}

export function sanitizeName(value: string | null) {
  return (value || "Player").trim().replace(/\s+/g, " ").replace(/[<>]/g, "").slice(0, 24) || "Player";
}
